import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import { isEmpty, isNil } from "lodash";

import { AuthActionStatusAtom, authActionStatusAtom, authErrorAtom } from "Recoil/auth.atom";
import { authGetVerify, authResendVerifyEmail, authVerifyEmail } from "Services/auth.services";

import VerifyEmailView from "Components/auth/verify-email-view";

import "./verify-account.scss";
import useQuery from "Hooks/query.hook";
import VerifyAccount from "Components/auth/verify-account";
import { toaster } from "Helpers/toaster";
import { RiderType } from "Types/new_driver_types";
import { UserRoleToAssign } from "Types/user.types";
import ConfirmStatus from "Components/shared/confirm-status";
import { Spin } from "antd";

interface Input {
  emailToken: string;
  passToken?: string;
  key?: string;
  email?: string;
  riderType?: RiderType;
  subType?: UserRoleToAssign;
  orgEmail?: string;
}

const VerifyAccountPage = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const query = useQuery();

  const [status, setStatus] = useRecoilState<AuthActionStatusAtom>(authActionStatusAtom);
  const [error, setError] = useRecoilState<any>(authErrorAtom);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [isAlreadyVerified, setIsAlreadyVerified] = useState<boolean>(false);
  const [pathToChoose, setPathToChoose] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [token, setToken] = useState<any>();
  const email = query.get("email") || "";

  useEffect(() => {
    handleGetVerify(email);
  }, []);

  useEffect(() => {
    if (status.resendVerifyEmail === "after_success") {
      setStatus({ ...status, resendVerifyEmail: "idle" });
    }
  }, [status]);

  const handleGetVerify = async (email) => {
    setIsLoading && setIsLoading(true);
    const responseData: any = await authGetVerify(email);

    if (responseData && !responseData.error) {
      if (responseData?.data?.verified) {
        setIsAlreadyVerified && setIsAlreadyVerified(true);
      }
    } else {
      toaster("error", responseData.error.message);
    }

    setIsLoading && setIsLoading(false);
  };

  const handleVerifyEmail = async ({
    emailToken,
    passToken,
    key,
    email,
    riderType,
    subType,
    orgEmail,
  }: Input) => {
    setStatus({ ...status, verifyEmail: "loading" });
    const responseData = await authVerifyEmail({
      verifyToken: emailToken,
      key,
      email,
      riderType,
      subType,
      orgEmail,
    });

    if (!isEmpty(responseData.error)) {
      setStatus({ ...status, verifyEmail: "error" });
      toaster("error", responseData.error.message);
    } else {
      setStatus({ ...status, verifyEmail: "success" });
      if (passToken) {
        setPathToChoose && setPathToChoose(2);
        setIsVerified && setIsVerified(true);
        setToken && setToken(passToken);
      } else {
        setPathToChoose && setPathToChoose(3);
        setIsVerified && setIsVerified(true);
        //SET NEW PASSWORD AFTER VERIFY ACCOUNT
        setToken && setToken(emailToken);
      }
    }
  };

  const handleResendEmail = async () => {
    setStatus({ ...status, resendVerifyEmail: "loading" });
    const responseData = await authResendVerifyEmail({ email });

    if (!isNil(responseData.error)) {
      setStatus({ ...status, resendVerifyEmail: "error" });
      toaster("error", responseData?.error?.message);
      setError(responseData?.error);
    } else {
      setStatus({ ...status, resendVerifyEmail: "success" });
      toaster("success", t("toaster.checkEmail"));
    }
  };

  const changeURL = (type) => {
    switch (type) {
      case 1:
        history.push("/forget-password");
        break;
      case 2:
        history.push({
          pathname: "/reset-password",
          search: `?email=${email}&passToken=${token}`,
        });
        break;
      case 3:
        history.push({
          pathname: "/reset-password",
          search: `?email=${email}&passToken=${token}`,
        });
        break;
      default:
        history.push("/login");
    }
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>{t("pageTitle.verifyAccount")}</title>
      </Helmet>

      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {isAlreadyVerified ? (
            <div className="verify-account-page--full-height">
              <ConfirmStatus
                title={t("txt.accountVerified")}
                subtitle={t("txt.accountAlreadyVerifiedSubtitle")}
                btnPrm={{
                  btnPrmText: t("txt.returnToLogin"),
                  btnPrmFunc: () => changeURL(0),
                }}
                btnSec={{
                  btnSecText: t("action.changePassword"),
                  btnSecFunc: () => changeURL(1),
                }}
              />
            </div>
          ) : isVerified ? (
            <div className="verify-account-page--full-height">
              <ConfirmStatus
                title={t("txt.accountVerified")}
                subtitle={t("txt.accountVerifiedSubtitle")}
                btnPrm={{
                  btnPrmText: t("txt.setUpAccount"),
                  btnPrmFunc: () => changeURL(pathToChoose),
                }}
              />
            </div>
          ) : (
            <div className="verify-account-page">
              <div className="verify-account-page--header"></div>
              <img
                alt={t("alt.logo")}
                src="/assets/images/banner_logo.png"
                style={{
                  height: "60px",
                  width: "100%",
                  maxWidth: "500px",
                  margin: "60px auto 0",
                }}
              />
              <div className="verify-account-page--main">
                <div className="verify-account-page--container">
                  <div className="verify-account-page--view">
                    <div className="verify-account-page--view-title">
                      <h3>{t("title.verifyAccount")}</h3>
                    </div>

                    <div
                      className="verify-account-page--form-description"
                      style={{ textAlign: "center" }}
                      dangerouslySetInnerHTML={{
                        __html: t("txt.verifyAccountFourDigitCode") + decodeURIComponent(email),
                      }}
                    />

                    <VerifyAccount
                      status={status.verifyEmail}
                      onSetVerifyToken={handleVerifyEmail}
                      onResendEmail={handleResendEmail}
                    />
                  </div>
                  <div className="verify-account-page--bot"></div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </React.Fragment>
  );
};

export default VerifyAccountPage;
